import React from "react";
import { motion } from "motion/react";
import { 
  AlertTriangle, 
  Camera, 
  Upload, 
  Check, 
  Layers, 
  Trash2, 
  Sparkles, 
  Info,
  ChevronRight,
  EyeOff
} from "lucide-react";

interface AIValidationCardProps {
  key?: string;
  type: "not_plant" | "low_quality" | "multiple_plants" | "low_confidence" | "unsupported_format";
  imagePreview: string | null;
  qualityScore?: "Excellent" | "Good" | "Fair" | "Poor";
  onReset: () => void;
  onContinue?: () => void;
  onCameraScan?: () => void;
}

export default function AIValidationCard({
  type,
  imagePreview,
  qualityScore = "Poor",
  onReset,
  onContinue,
  onCameraScan
}: AIValidationCardProps) {
  
  // Render suggestions list
  const suggestions = [
    "Upload close-up leaf photos",
    "Use good lighting",
    "Avoid blurry images",
    "Focus on a single plant",
    "Remove background clutter"
  ];

  if (type === "unsupported_format") {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl mx-auto bg-white rounded-3xl border border-amber-150 p-6 md:p-8 shadow-xl relative overflow-hidden text-left"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50 rounded-full blur-3xl opacity-40 pointer-events-none" />
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
          <div className="md:col-span-2 flex flex-col items-center justify-center p-6 bg-amber-50/40 rounded-2xl border border-dashed border-amber-250">
            <AlertTriangle className="w-12 h-12 text-amber-550 animate-pulse" />
            <span className="text-xs font-mono font-black text-amber-800 uppercase tracking-widest mt-3">Unsupported format</span>
          </div>

          <div className="md:col-span-3 space-y-4">
            <div className="space-y-1.5">
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-600 block font-mono">File system warning</span>
              <h4 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight leading-tight">Unsupported File Format!</h4>
              <p className="text-xs md:text-sm text-gray-650 leading-relaxed font-semibold">
                Please upload an image of the supported formats: <strong className="text-gray-900">PNG, JPG, JPEG, WEBP, HEIC</strong>.
              </p>
            </div>

            <div className="bg-amber-50/20 border border-amber-100 rounded-xl p-4 text-xs font-semibold text-gray-600 leading-relaxed">
              Our botanical system optimizes plant identifications using file standard headers. Common picture types reduces analysis uncertainty.
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              <button
                onClick={onReset}
                className="w-full sm:w-auto px-5 py-3 bg-amber-600 hover:bg-amber-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <Upload className="w-3.5 h-3.5" />
                <span>Upload Supported Image</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (type === "not_plant") {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl mx-auto bg-white rounded-3xl border border-rose-150 p-6 md:p-8 shadow-xl relative overflow-hidden"
      >
        {/* Top subtle visual graphic */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-rose-50/50 rounded-full blur-2xl pointer-events-none" />
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
          {/* Diagnostic Specimen Frame */}
          <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden border border-gray-150 shadow-inner bg-gray-50 flex items-center justify-center">
            {imagePreview ? (
              <img 
                src={imagePreview} 
                alt="Uploaded Specimen" 
                className="w-full h-full object-cover grayscale brightness-90 contrast-95"
                referrerPolicy="no-referrer"
              />
            ) : (
              <EyeOff className="w-8 h-8 text-rose-300" />
            )}
            <div className="absolute top-3 left-3 bg-rose-600/90 text-white font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> No Specimen Detected
            </div>
          </div>

          {/* Report Metadata and Suggestions */}
          <div className="md:col-span-3 text-left space-y-4">
            <div className="space-y-1.5">
              <span className="text-[10px] font-black tracking-widest uppercase text-rose-500 block">
                Validation Alert
              </span>
              <h4 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight leading-tight">
                No Plant Detected
              </h4>
              <p className="text-xs md:text-sm text-gray-650 leading-relaxed font-semibold">
                We could not detect a recognizable plant or botanical object in this image. Please upload a clearer plant, flower, leaf, or tree photo for AI analysis.
              </p>
            </div>

            {/* Structured Guidelines bullet box */}
            <div className="bg-rose-50/20 border border-rose-100 rounded-xl p-4 space-y-2">
              <span className="text-[10px] font-black text-rose-800 uppercase tracking-wider block">
                Recommended Scan Guidelines:
              </span>
              <ul className="space-y-1.5">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs text-gray-700 font-bold">
                    <span className="w-4 h-4 rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-[10px] font-bold shrink-0">
                      ✓
                    </span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Actions button dock */}
            <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
              <button
                onClick={onReset}
                className="w-full sm:w-auto px-5 py-3 bg-rose-650 hover:bg-rose-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload Another Photo
              </button>
              {onCameraScan && (
                <button
                  onClick={onCameraScan}
                  className="w-full sm:w-auto px-5 py-3 bg-gray-55 hover:bg-gray-100 border border-gray-250 text-gray-700 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
                >
                  <Camera className="w-3.5 h-3.5" />
                  Try Camera Scan
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (type === "low_quality") {
    const scores: Array<"Excellent" | "Good" | "Fair" | "Poor"> = ["Excellent", "Good", "Fair", "Poor"];
    
    return (
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl mx-auto bg-white rounded-3xl border border-amber-150 p-6 md:p-8 shadow-xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-amber-50/50 rounded-full blur-2xl pointer-events-none" />
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
          {/* Specimen Frame */}
          <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden border border-gray-150 shadow-inner bg-gray-50">
            {imagePreview && (
              <img 
                src={imagePreview} 
                alt="Low quality target" 
                className="w-full h-full object-cover blur-xs brightness-95"
                referrerPolicy="no-referrer"
              />
            )}
            <div className="absolute top-3 left-3 bg-amber-500 text-white font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
              <AlertTriangle className="w-2.5 h-2.5" /> Low Specimen Details
            </div>
          </div>

          {/* Validation Details */}
          <div className="md:col-span-3 text-left space-y-4">
            <div className="space-y-1.5">
              <span className="text-[10px] font-black tracking-widest uppercase text-amber-500 block">
                Quality Verification Warning
              </span>
              <h4 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight leading-tight">
                Image Quality Too Low
              </h4>
              <p className="text-xs md:text-sm text-gray-650 leading-relaxed font-semibold">
                The uploaded image is too unclear for accurate plant identification. Please upload a sharper image with better lighting.
              </p>
            </div>

            {/* Quality Score segments indicator */}
            <div className="bg-gray-50 border border-gray-150 rounded-2xl p-4 space-y-2.5">
              <div className="flex justify-between items-center text-xs font-black uppercase text-gray-400">
                <span>Verification Score:</span>
                <span className="text-amber-600 font-mono font-bold uppercase">{qualityScore}</span>
              </div>
              
              {/* Score Steps Card */}
              <div className="grid grid-cols-4 gap-1.5">
                {scores.map((scoreStep) => {
                  const isActive = qualityScore === scoreStep;
                  const isRed = scoreStep === "Poor";
                  const isYellow = scoreStep === "Fair";
                  const isGreen = scoreStep === "Good" || scoreStep === "Excellent";
                  
                  return (
                    <div key={scoreStep} className="space-y-1">
                      <div className={`h-2 rounded-full transition-all ${
                        isActive 
                          ? isRed ? "bg-red-500 scale-y-110" 
                            : isYellow ? "bg-amber-500 scale-y-110"
                            : "bg-emerald-500 scale-y-110"
                          : "bg-gray-200"
                      }`} />
                      <span className={`text-[9px] font-black block text-center tracking-tight transition-colors ${
                        isActive ? "text-gray-950 font-bold" : "text-gray-400"
                      }`}>
                        {scoreStep}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bottom Controls */}
            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              <button
                onClick={onReset}
                className="w-full sm:w-auto px-5 py-3 bg-amber-600 hover:bg-amber-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload Another Photo
              </button>
              {onContinue && (
                <button
                  onClick={onContinue}
                  className="w-full sm:w-auto px-5 py-3 bg-gray-55 hover:bg-gray-100 border border-gray-200 text-gray-700 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
                >
                  <span>Continue Anyway</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  if (type === "multiple_plants") {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-2xl mx-auto bg-white rounded-3xl border border-indigo-150 p-6 md:p-8 shadow-xl relative overflow-hidden"
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 rounded-full blur-2xl pointer-events-none" />
        
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
          {/* Specimen Frame */}
          <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden border border-gray-150 shadow-inner bg-gray-50 flex items-center justify-center">
            {imagePreview && (
              <img 
                src={imagePreview} 
                alt="Multiple plants" 
                className="w-full h-full object-cover brightness-95"
                referrerPolicy="no-referrer"
              />
            )}
            <div className="absolute top-3 left-3 bg-indigo-600 text-white font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
              <Layers className="w-2.5 h-2.5" /> Multiple Specimen
            </div>
          </div>

          {/* Validation Details */}
          <div className="md:col-span-3 text-left space-y-4">
            <div className="space-y-1.5">
              <span className="text-[10px] font-black tracking-widest uppercase text-indigo-500 block">
                Focal Alert
              </span>
              <h4 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight leading-tight">
                Multiple Plants Detected
              </h4>
              <p className="text-xs md:text-sm text-gray-650 leading-relaxed font-semibold">
                For best accuracy, upload a photo focused on one plant only.
              </p>
            </div>

            <div className="bg-indigo-50/20 border border-indigo-100 rounded-xl p-4 text-xs font-semibold text-gray-600 leading-relaxed">
              Our AI found multiple botanical layers here. If you proceed, the system will identify the primary or central foliage species detected.
            </div>

            {/* Bottom Controls */}
            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              {onContinue && (
                <button
                  onClick={onContinue}
                  className="w-full sm:w-auto px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
                >
                  <span>Continue Anyway</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={onReset}
                className="w-full sm:w-auto px-5 py-3 bg-gray-55 hover:bg-gray-100 border border-gray-250 text-gray-750 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
              >
                <Upload className="w-3.5 h-3.5 text-gray-500" />
                Upload Another Photo
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Low confidence warning
  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-2xl mx-auto bg-white rounded-3xl border border-sky-150 p-6 md:p-8 shadow-xl relative overflow-hidden"
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-sky-50/55 rounded-full blur-2xl pointer-events-none" />
      
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-center">
        {/* Specimen Frame */}
        <div className="md:col-span-2 relative aspect-square rounded-2xl overflow-hidden border border-gray-150 shadow-inner bg-gray-50">
          {imagePreview && (
            <img 
              src={imagePreview} 
              alt="Low identification confidence" 
              className="w-full h-full object-cover brightness-95 opacity-90"
              referrerPolicy="no-referrer"
            />
          )}
          <div className="absolute top-3 left-3 bg-sky-550 text-white font-extrabold text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-md shadow-xs flex items-center gap-1">
            <Info className="w-2.5 h-2.5" /> Specimen Ambiguity
          </div>
        </div>

        {/* Validation Details */}
        <div className="md:col-span-3 text-left space-y-4">
          <div className="space-y-1.5">
            <span className="text-[10px] font-black tracking-widest uppercase text-sky-650 block">
              Low Certainty Alert
            </span>
            <h4 className="text-xl md:text-2xl font-black text-gray-950 tracking-tight leading-tight">
              Unable to confidently identify this plant
            </h4>
            <p className="text-xs md:text-sm text-gray-650 leading-relaxed font-semibold">
              This result may not be fully accurate. Try uploading a clearer image for better identification.
            </p>
          </div>

          <div className="bg-sky-50/20 border border-sky-100 rounded-xl p-4 text-xs font-semibold text-gray-600 leading-relaxed">
            Our AI engine indicates botanical ambiguity in the uploaded material. Clearer focus on leaf nodes or flower segments reduces analysis uncertainty.
          </div>

          {/* Bottom Controls */}
          <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
            {onContinue && (
              <button
                onClick={onContinue}
                className="w-full sm:w-auto px-5 py-3 bg-sky-600 hover:bg-sky-700 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xs flex items-center justify-center gap-1.5 cursor-pointer active:scale-98"
              >
                <span>View LowCertainty Match</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={onReset}
              className="w-full sm:w-auto px-5 py-3 bg-gray-55 hover:bg-gray-100 border border-gray-250 text-gray-750 font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer active:scale-98"
            >
              <Upload className="w-3.5 h-3.5 text-gray-500" />
              Try Another Specimen
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
